// frontend/src/components/dashboard/pos/components/TopAppBar.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Chip,
  Avatar,
  useTheme
} from '@mui/material';
import {
  ShoppingCart,
  AttachMoney,
  Payment,
  Wifi,
  WifiOff
} from '@mui/icons-material';
import { gradients } from '../styles/posStyles';

const TopAppBar = ({
  vendorInfo,
  cartItems,
  calculateTotal,
  isOnline,
  onBackToDashboard,
  onPayment,
  isMobile
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const total = calculateTotal();

  return (
    <AppBar 
      position="static" 
      color="default" 
      elevation={0}
      sx={{ 
        bgcolor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
        zIndex: 1100
      }}
    >
      <Toolbar sx={{ 
        minHeight: { xs: 56, sm: 64 },
        px: { xs: 1, sm: 2 }
      }}>
        {/* Left side - Back button (only on mobile/tablet) */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          flex: { xs: '0 0 auto', md: '0 0 30%' }
        }}>
          <IconButton
            onClick={onBackToDashboard}
            sx={{
              mr: { xs: 1, sm: 2 },
              backgroundColor: 'rgba(102, 126, 234, 0.15)',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.25)',
              },
              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)',
              borderRadius: '12px',
              p: { xs: 0.8, sm: 1.2 },
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 }
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                color: '#667eea',
              }}
            >
              <svg 
                width={isMobile ? "20" : "24"} 
                height={isMobile ? "20" : "24"} 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
          </IconButton>
          
          {/* Vendor Logo - Hidden on small mobile */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {vendorInfo.logo ? (
              <Avatar 
                src={vendorInfo.logo} 
                alt={vendorInfo.vendorName || 'Logo'} 
                sx={{ width: { sm: 36, md: 40 }, height: { sm: 36, md: 40 } }}
              />
            ) : (
              <Avatar 
                sx={{ 
                  width: { sm: 36, md: 40 }, 
                  height: { sm: 36, md: 40 },
                  bgcolor: theme.palette.primary.main,
                  fontSize: { sm: '1rem', md: '1.2rem' }
                }}
              >
                {(vendorInfo.vendorName?.[0] || 'P')}
              </Avatar>
            )}
          </Box>
        </Box>
        
        {/* Center - Vendor name */}
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            px: { xs: 1, sm: 2 }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              textAlign: 'center',
              color: theme.palette.text.primary,
              fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
              lineHeight: 1.2,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: { xs: 'nowrap', sm: 'normal' }
            }}
          >
            {vendorInfo.vendorName || 'Vendor Name'}
            {vendorInfo.locationName && (
              <Box 
                component="span" 
                sx={{ 
                  display: { xs: 'none', sm: 'inline' },
                  fontSize: '0.9em',
                  fontWeight: 400,
                  opacity: 0.8
                }}
              >
                {' - '}{vendorInfo.locationName}
              </Box>
            )}
          </Typography>
        </Box>
        
        {/* Right side - Cart info, network status, and payment button */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1, md: 2 },
          flex: { xs: '0 0 auto', md: '0 0 30%' },
          justifyContent: 'flex-end'
        }}>
          {/* Network Status Indicator */}
          <IconButton 
            size="small" 
            color={isOnline ? 'success' : 'error'}
            sx={{ 
              width: { xs: 32, sm: 36 },
              height: { xs: 32, sm: 36 }
            }}
          >
            {isOnline ? <Wifi fontSize="small" /> : <WifiOff fontSize="small" />}
          </IconButton>
          
          {/* Cart Items Count - Hidden on small mobile */}
          <Chip
            icon={<ShoppingCart />}
            label={`${cartItems.length} ${t('pos.items')}`}
            color="primary"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{ 
              display: { xs: 'none', sm: 'flex', md: 'flex' },
              fontSize: { sm: '0.75rem', md: '0.875rem' }
            }}
          />
          
          {/* Total Amount */}
          <Chip
            icon={<AttachMoney />}
            label={`$${total.total.toFixed(2)}`}
            color="primary"
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              '& .MuiChip-label': {
                px: { xs: 1, sm: 1.5 }
              }
            }}
            size={isMobile ? "small" : "medium"}
          />
          
          {/* Pay Now Button - Hidden on mobile
          <Button 
            variant="contained" 
            startIcon={!isMobile && <Payment />}
            onClick={onPayment}
            disabled={cartItems.length === 0 || !isOnline}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              background: gradients.primary,
              '&:hover': {
                background: gradients.primaryHover
              },
              display: { xs: 'none', md: 'flex' },
              fontSize: { md: '0.875rem', lg: '1rem' },
              px: { md: 2, lg: 3 }
            }}
          >
            {t('pos.payNow')}
          </Button> */}
          
          {/* Mobile Pay Button - Compact */}
          <Button 
            variant="contained" 
            onClick={onPayment}
            disabled={cartItems.length === 0 || !isOnline}
            size="small"
            sx={{ 
              background: gradients.primary,
              '&:hover': {
                background: gradients.primaryHover
              },
              display: { xs: 'flex', md: 'none' },
              minWidth: 'auto',
              px: 2,
              fontSize: '0.75rem'
            }}
          >
            {t('pos.pay')}
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopAppBar;